import { useProjects } from '@/hooks/use-projects';
import { useCurrentProjectStore } from '@/stores/current-project';
import { useEffect } from 'react';
import { useMatch } from 'react-router-dom';

/**
 * Single source of truth for the current project public_id.
 *
 * Three things feed it, in order: the `/projects/:id/*` route param (using
 * `useParams` naively would also pick up `:id` from `/events/:id`,
 * `/cases/:id`, `/devices/:id`, which are NOT project ids and would corrupt the
 * store — e.g. setting currentProjectId='2606-5' from an event URL, breaking
 * downstream `/cases?project=...` calls), the choice this reader last made, and
 * failing both, their first project, so the rail's project rows lead somewhere
 * on a first visit.
 */
export function useCurrentProjectId(): string | null {
  const match = useMatch('/projects/:id/*');
  const urlProjectId = match?.params.id ?? null;
  const stored = useCurrentProjectStore((s) => s.currentProjectId);
  const setStored = useCurrentProjectStore((s) => s.setCurrentProjectId);
  const { data: projects } = useProjects();
  const firstProjectId = projects?.[0]?.public_id ?? null;

  useEffect(() => {
    if (urlProjectId) {
      if (urlProjectId !== stored) setStored(urlProjectId);
      return;
    }
    // A reader who has never opened a project still needs one in hand: the
    // rail's project rows (and the events list's "this project" scope) are dead
    // ends without it, so the console defaults to their first project. A
    // remembered choice is never overridden, and leaving every project is what
    // `/events` with no `?project=` already means — this is a scope for the
    // *frame*, not a filter on the page (spec §27).
    if (!stored && firstProjectId) setStored(firstProjectId);
  }, [urlProjectId, stored, firstProjectId, setStored]);

  return urlProjectId ?? stored ?? firstProjectId;
}

/**
 * Publish the project a page belongs to into the store.
 *
 * Three kinds of page hand the scope over, and each learns it somewhere else:
 * the lists from `?project=`, `/projects/:id/*` from ProjectLayout's route
 * param, and a detail page from the record it just loaded. Writing it down is
 * what keeps the rail's project nav and the band's selector describing the page
 * in front of the reader — deep-linking `/events/2606-1` used to leave the
 * whole console looking like nothing was selected, even though the page is
 * plainly project #1's.
 *
 * Silent while the record loads, and it never clears: a page that does not know
 * its project must leave the reader's last selection alone.
 */
export function usePublishProjectScope(publicId: string | null | undefined) {
  const setStored = useCurrentProjectStore((s) => s.setCurrentProjectId);

  useEffect(() => {
    if (publicId) setStored(publicId);
  }, [publicId, setStored]);
}
